import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { membersApi } from "../../../../../api/members/membersApi";
import {
  Member,
  MemberFilters,
  CreateMemberRequest,
  UpdateMemberRequest,
  ImportMembersRequest,
  MemberTransaction,
  UpdateTransactionRequest,
} from "../../../../../api/members/types";

interface MembersState {
  members: Member[];
  selectedMember: Member | null;
  transactions: MemberTransaction[];
  loading: boolean;
  importLoading: boolean;
  error: string | null;
  total: number;
  currentPage: number;
  perPage: number;
  filters: MemberFilters;
  importProgress: {
    status: "idle" | "reading" | "parsing" | "importing" | "error" | "success";
    message: string;
    progress: number;
  };
}

const initialState: MembersState = {
  members: [],
  selectedMember: null,
  transactions: [],
  loading: false,
  importLoading: false,
  error: null,
  total: 0,
  currentPage: 1,
  perPage: 25,
  filters: {},
  importProgress: {
    status: "idle",
    message: "",
    progress: 0,
  },
};

// Async thunks
export const fetchMembersAction = createAsyncThunk(
  "members/fetchMembers",
  async (filters?: MemberFilters) => {
    const response = await membersApi.fetchMembers(filters);
    return response;
  },
);

export const fetchMemberAction = createAsyncThunk(
  "members/fetchMember",
  async (id: string) => {
    const member = await membersApi.fetchMember(id);
    return member;
  },
);

export const createMemberAction = createAsyncThunk(
  "members/createMember",
  async (data: CreateMemberRequest) => {
    const member = await membersApi.createMember(data);
    return member;
  },
);

export const updateMemberAction = createAsyncThunk(
  "members/updateMember",
  async (data: UpdateMemberRequest) => {
    const { id, ...updateData } = data;
    const member = await membersApi.updateMember(id, updateData);
    return member;
  },
);

export const deleteMemberAction = createAsyncThunk(
  "members/deleteMember",
  async (id: string) => {
    await membersApi.deleteMember(id);
    return id;
  },
);

export const importMembersAction = createAsyncThunk(
  "members/importMembers",
  async (data: ImportMembersRequest, { dispatch }) => {
    dispatch(
      setImportProgress({
        status: "importing",
        message: "Importing members...",
        progress: 60,
      }),
    );

    const response = await membersApi.importMembers(data);

    if (response.success) {
      dispatch(
        setImportProgress({
          status: "success",
          message: response.message || "Import completed successfully",
          progress: 100,
        }),
      );
    }

    return response;
  },
);

export const fetchMemberTransactionsAction = createAsyncThunk(
  "members/fetchTransactions",
  async (memberId: string) => {
    const transactions = await membersApi.fetchMemberTransactions(memberId);
    return transactions;
  },
);

export const updateTransactionAction = createAsyncThunk(
  "members/updateTransaction",
  async (data: UpdateTransactionRequest) => {
    const transaction = await membersApi.updateTransaction(data);
    return transaction;
  },
);

// Slice
const membersSlice = createSlice({
  name: "members",
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<MemberFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.perPage = action.payload;
    },
    clearSelectedMember: (state) => {
      state.selectedMember = null;
      state.transactions = [];
    },
    setImportProgress: (
      state,
      action: PayloadAction<MembersState["importProgress"]>,
    ) => {
      state.importProgress = action.payload;
    },
    resetImportProgress: (state) => {
      state.importProgress = {
        status: "idle",
        message: "",
        progress: 0,
      };
    },
  },
  extraReducers: (builder) => {
    // Fetch members
    builder
      .addCase(fetchMembersAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMembersAction.fulfilled, (state, action) => {
        state.loading = false;
        state.members = action.payload.data;
        state.total = action.payload.total;
        state.currentPage = action.payload.page;
        state.perPage = action.payload.perPage;
      })
      .addCase(fetchMembersAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch members";
      });

    // Fetch single member
    builder
      .addCase(fetchMemberAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMemberAction.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMember = action.payload;
      })
      .addCase(fetchMemberAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch member";
      });

    // Create member
    builder
      .addCase(createMemberAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMemberAction.fulfilled, (state, action) => {
        state.loading = false;
        state.members.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createMemberAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create member";
      });

    // Update member
    builder
      .addCase(updateMemberAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMemberAction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.members.findIndex(
          (m) => m.id === action.payload.id,
        );
        if (index !== -1) {
          state.members[index] = action.payload;
        }
        if (state.selectedMember?.id === action.payload.id) {
          state.selectedMember = action.payload;
        }
      })
      .addCase(updateMemberAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update member";
      });

    // Delete member
    builder
      .addCase(deleteMemberAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMemberAction.fulfilled, (state, action) => {
        state.loading = false;
        state.members = state.members.filter((m) => m.id !== action.payload);
        state.total -= 1;
        if (state.selectedMember?.id === action.payload) {
          state.selectedMember = null;
        }
      })
      .addCase(deleteMemberAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete member";
      });

    // Import members
    builder
      .addCase(importMembersAction.pending, (state) => {
        state.importLoading = true;
        state.error = null;
      })
      .addCase(importMembersAction.fulfilled, (state, action) => {
        state.importLoading = false;
        if (!action.payload.success) {
          state.error = action.payload.message || "Import failed";
          state.importProgress = {
            status: "error",
            message: action.payload.message || "Import failed",
            progress: 0,
          };
        }
      })
      .addCase(importMembersAction.rejected, (state, action) => {
        state.importLoading = false;
        state.error = action.error.message || "Failed to import members";
        state.importProgress = {
          status: "error",
          message: action.error.message || "Failed to import members",
          progress: 0,
        };
      });

    // Fetch transactions
    builder
      .addCase(fetchMemberTransactionsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMemberTransactionsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchMemberTransactionsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch transactions";
      });

    // Update transaction
    builder
      .addCase(updateTransactionAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransactionAction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.transactions.findIndex(
          (t) => t.id === action.payload.id,
        );
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
      })
      .addCase(updateTransactionAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update transaction";
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentPage,
  setPerPage,
  clearSelectedMember,
  setImportProgress,
  resetImportProgress,
} = membersSlice.actions;

export default membersSlice.reducer;
